import { APIMethods, apiProxy, TResponse } from 'utils/api/apiRequestProxy';
import {
    ChannelResult,
    CreatedInstallerLink,
    InstallerLink,
    InstallerLinkEvent,
    InstallerLinkEventWire,
    InstallerLinkHistoryResponse,
    InstallerLinkResponse,
    InstallerLinksResponse,
    InstallerLinkWire,
    RequestCreateInstallerLink,
} from './types';

const URI = '/installer-links';

/** The one place snake_case API link objects become the tab's type. */
function mapLink(l: InstallerLinkWire): InstallerLink {
    return {
        id: l.id,
        recipientName: l.recipient_name,
        recipientPhone: l.recipient_phone ?? null,
        recipientEmail: l.recipient_email ?? null,
        createdByLabel: l.created_by_label,
        siteAddress: l.site_address,
        siteTimezone: l.site_timezone,
        createdAt: l.created_at,
        expiresAt: l.expires_at,
        endedAt: l.ended_at ?? null,
        status: l.status,
        endReason: l.end_reason ?? null,
    };
}

/** Top-level keys only: `details` keeps the camelCase keys it was stored with, which is what historyRow reads. */
function mapEvent(e: InstallerLinkEventWire): InstallerLinkEvent {
    return {
        id: e.id,
        type: e.type,
        at: e.at,
        actorLabel: e.actor_label ?? null,
        deviceSummary: e.device_summary ?? null,
        accessPointId: e.access_point_id ?? null,
        accessPointName: e.access_point_name ?? null,
        details: e.details ?? null,
    };
}

/** Keeps the response envelope (metadata, message) and replaces only the body. */
function withBody<W, T>(res: TResponse<W>, map: (body: W | undefined) => T): TResponse<T> {
    return { ...res, respData: map(res.respData) };
}

/**
 * Whether Swiftlane has turned Installer access on for this admin's account (a Command Center switch).
 * No toast: a failed check just leaves the tab hidden. Only a literal `enabled: true` counts.
 */
export async function requestInstallerAccessEnabled(): Promise<boolean> {
    const res = await apiProxy<{ enabled?: unknown }>({
        url: `${URI}/enabled`,
        method: APIMethods.GET,
        showToastErrMsg: false,
    });
    return res.respData?.enabled === true;
}

export async function requestInstallerLinks(): Promise<TResponse<InstallerLinksResponse>> {
    const res = await apiProxy<{ links?: InstallerLinkWire[] }>({
        url: URI,
        method: APIMethods.GET,
    });
    return withBody(res, body => ({ links: (body?.links ?? []).map(mapLink) }));
}

export async function requestCreateInstallerLink(data: RequestCreateInstallerLink): Promise<TResponse<CreatedInstallerLink>> {
    const res = await apiProxy<{ link: InstallerLinkWire; url: string; sms?: ChannelResult; email?: ChannelResult }>({
        url: URI,
        method: APIMethods.POST,
        data,
        // The Send dialog shows a failed create inline: one error, not an inline alert plus a toast.
        showToastErrMsg: false,
    });
    return {
        ...res,
        respData: res.respData
            ? { link: mapLink(res.respData.link), url: res.respData.url, sms: res.respData.sms, email: res.respData.email }
            : undefined,
    };
}

export async function requestDeactivateInstallerLink(id: string): Promise<TResponse<InstallerLinkResponse>> {
    const res = await apiProxy<{ link: InstallerLinkWire }>({
        url: `${URI}/${id}/deactivate`,
        method: APIMethods.POST,
        // The tab toasts this itself: "already ended" is a notice, everything else an error.
        showToastErrMsg: false,
    });
    return {
        ...res,
        respData: res.respData ? { link: mapLink(res.respData.link) } : undefined,
    };
}

export async function requestInstallerLinkHistory(id: string): Promise<TResponse<InstallerLinkHistoryResponse>> {
    const res = await apiProxy<{ events?: InstallerLinkEventWire[] }>({
        url: `${URI}/${id}/history`,
        method: APIMethods.GET,
    });
    return withBody(res, body => ({ events: (body?.events ?? []).map(mapEvent) }));
}
